import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../common/Header.js';
import Sidebar from '../common/Sidebar.js';
import Alert from '../common/Alert.js';
import { testimonialService } from '../../services/testimonialService.js';
import { MessageSquareQuote, Plus, Eye, Pencil, Trash } from "lucide-react";

const TestimonialList = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [imageErrors, setImageErrors] = useState({});
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 10,
    total: 0,
    pages: 0
  });
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchTestimonials();
  }, [pagination.page]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchTestimonials = async () => {
    try {
      setLoading(true);
      const response = await testimonialService.getAllTestimonials(
        pagination.page,
        pagination.per_page,
        false,
        true
      );
      setTestimonials(response.data.data || []);
      setPagination(prev => ({
        ...prev,
        total: response.data.pagination.total,
        pages: response.data.pagination.pages
      }));
    } catch (err) {
      Alert.error('Failed to load testimonials');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (testimonialId, testimonialName) => {
    const confirmed = await Alert.confirm(
      `Are you sure you want to delete the testimonial from "${testimonialName}"? This action cannot be undone.`,
      'Delete Testimonial'
    );
    if (confirmed) {
      try {
        await testimonialService.deleteTestimonial(testimonialId);
        Alert.success('Testimonial deleted successfully!');
        setActiveDropdown(null);
        fetchTestimonials();
      } catch (err) {
        Alert.error(err.response?.data?.error || 'Failed to delete testimonial');
      }
    }
  };

  const toggleDropdown = (e, testimonialId) => {
    e.stopPropagation();
    setActiveDropdown(activeDropdown === testimonialId ? null : testimonialId);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.pages) {
      setPagination(prev => ({ ...prev, page: newPage }));
      setActiveDropdown(null);
    }
  };

  const renderStars = (rating) => {
    return '⭐'.repeat(rating);
  };

  const getImageSrc = (testimonial) => {
    if (testimonial.image_base64) {
      return `data:image/jpeg;base64,${testimonial.image_base64}`;
    }
    return null;
  };

  const renderProfileImage = (testimonial) => {
    const imageSrc = getImageSrc(testimonial);
    const hasError = imageErrors[testimonial.id];
    if (imageSrc && !hasError) {
      return (
        <img
          src={imageSrc}
          alt={testimonial.full_name}
          className="w-full h-full object-cover"
          onError={() => setImageErrors(prev => ({ ...prev, [testimonial.id]: true }))}
        />
      );
    }
    return <span className="text-2xl">👤</span>;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <div className="max-w-[1850px] mx-auto">
      <Header />
      <div className="flex">
        <Sidebar />
        <div className="flex bg-light w-full mt-16 sm:mt-0">
          <div className="flex-1 flex flex-col overflow-hidden">
            <main className="flex-1 overflow-auto p-3 sm:p-4 md:p-6">
              <div className="max-w-8xl mx-auto">
                {/* Page Header */}
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-4 mb-4 sm:mb-4">
                  <div>
                    <h1 className="text-2xl sm:text-xl font-bold text-secondary flex items-center gap-2 sm:gap-3">
                      <MessageSquareQuote className="w-5 h-5 text-buttonBg" />
                      Testimonials
                    </h1>
                  </div>
                  <div className="flex flex-wrap gap-2 sm:gap-3">
                    <button
                      onClick={() => navigate('/testimonials/add')}
                      className="
                            relative overflow-hidden
                            group flex items-center justify-center gap-2
                            px-3 sm:px-3 py-2.5 sm:py-2
                            text-xs sm:text-sm
                            rounded-lg
                            border border-buttonBg
                            bg-buttonBg text-buttonTxt
                            font-medium whitespace-nowrap

                            transition-colors duration-300 ease-out
                            hover:bg-transparent hover:text-buttonBg
                          "
                        >
                          {/* COLLAPSING BACKGROUND */}
                          <span
                            className="
                              absolute inset-0
                              bg-buttonBg
                              scale-x-100
                              group-hover:scale-x-0
                              origin-center
                              transition-transform duration-300 ease-out
                              z-0
                            "
                          />

                          {/* ICON */}
                          <Plus
                            className="
                              w-4 h-4 relative z-10
                              transition-transform duration-300 ease-out
                              group-hover:scale-90
                            "
                          />

                          <span className="relative z-10">Add Testimonial</span>
                      <span className="sm:hidden">Add</span>
                    </button>
                  </div>
                </div>

                {/* Content */}
                <div className='hidden sm:block'>

                  {/* Table View - Desktop */}
                  <div className="hidden lg:block bg-white rounded-sm shadow-sm">
                    {loading ? (
                      <div className="p-12 text-center text-gray-600">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
                        Loading testimonials...
                      </div>
                    ) : testimonials.length === 0 ? (
                      <div className="p-12 text-center text-gray-600">No testimonials found</div>
                    ) : (
                      <>
                        <div className="hidden md:block w-full">
                                <table className="w-full table-fixed border-collapse">
                                  <thead>
                                    <tr className="bg-primary text-tertiary">
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Photo</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rating</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Message</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {testimonials.map((testimonial) => (
                                <tr key={testimonial.id} className="hover:bg-gray-50 transition-colors">
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
                                      {renderProfileImage(testimonial)}
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <button
                                      onClick={() => navigate(`/testimonials/view/${testimonial.id}`)}
                                      className="text-sm font-medium text-gray-900 hover:underline"
                                    >
                                      {testimonial.full_name}
                                    </button>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm text-gray-700">{testimonial.location || 'N/A'}</div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm text-yellow-500">{renderStars(testimonial.rating)}</div>
                                  </td>
                                  <td className="px-6 py-4">
                                    <div className="text-sm text-gray-700 max-w-xs truncate">{testimonial.message}</div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                      testimonial.is_active
                                        ? 'bg-green-100 text-green-800'
                                        : 'bg-gray-100 text-gray-800'
                                    }`}>
                                      {testimonial.is_active ? 'Active' : 'Inactive'}
                                    </span>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm text-gray-700">{formatDate(testimonial.created_at)}</div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium relative">
                                    <button
                                      onClick={(e) => toggleDropdown(e, testimonial.id)}
                                      className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-md transition-colors"
                                    >
                                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                                        <circle cx="12" cy="5" r="2"/>
                                        <circle cx="12" cy="12" r="2"/>
                                        <circle cx="12" cy="19" r="2"/>
                                      </svg>
                                    </button>

                                    {activeDropdown === testimonial.id && (
                                      <div
                                        ref={dropdownRef}
                                        className="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10"
                                      >
                                        <button
                                          onClick={(e) => { e.stopPropagation(); navigate(`/testimonials/view/${testimonial.id}`); setActiveDropdown(null); }}
                                          className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex gap-2 items-center border-b"
                                        >
                                          <Eye size={16} className="text-buttonBg" />
                                          <span className="text-base capitalize">View Details</span>
                                        </button>
                                        <button
                                          onClick={(e) => { e.stopPropagation(); navigate(`/testimonials/edit/${testimonial.id}`); setActiveDropdown(null); }}
                                          className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex gap-2 items-center border-b"
                                        >
                                          <Pencil size={16} className="text-buttonBg" />
                                          <span className="text-base capitalize">Edit</span>
                                        </button>
                                        <button
                                          onClick={(e) => { e.stopPropagation(); handleDelete(testimonial.id, testimonial.full_name); }}
                                          className="w-full px-4 py-2 text-left text-sm hover:bg-red-50 flex gap-2 items-center"
                                        >
                                          <Trash size={16} className="text-red-600" />
                                          <span className="text-base capitalize text-red-600">Delete</span>
                                        </button>
                                      </div>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>

                        {/* Pagination */}
                        <div className="hidden md:flex bg-gray-50 px-6 py-4 justify-between items-center border-t">
                          <div className="text-sm text-gray-700">
                            Showing {testimonials.length} of {pagination.total} testimonial{pagination.total !== 1 ? 's' : ''}
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => handlePageChange(pagination.page - 1)}
                              disabled={pagination.page === 1}
                              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition"
                            >
                              ← Previous
                            </button>
                            <span className="px-4 py-2 text-sm text-gray-700">
                              Page {pagination.page} of {pagination.pages}
                            </span>
                            <button
                              onClick={() => handlePageChange(pagination.page + 1)}
                              disabled={pagination.page === pagination.pages}
                              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition"
                            >
                              Next →
                            </button>
                          </div>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Card View - Mobile/Tablet */}
                  <div className="lg:hidden space-y-3 sm:space-y-4">
                    {loading ? (
                      <div className="bg-white p-8 rounded-lg shadow-sm text-center text-gray-600">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
                        Loading testimonials...
                      </div>
                    ) : testimonials.length === 0 ? (
                      <div className="bg-white p-8 rounded-lg shadow-sm text-center text-gray-600">No testimonials found</div>
                    ) : (
                      <>
                        {testimonials.map((testimonial) => (
                          <div key={testimonial.id} className="bg-white rounded-lg shadow-sm p-4 sm:p-5">
                            <div className="flex items-start gap-3 sm:gap-4 mb-3">
                              <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden flex-shrink-0">
                                {renderProfileImage(testimonial)}
                              </div>
                              <div className="flex-1 min-w-0">
                                <h3 className="font-semibold text-gray-900 text-base sm:text-lg truncate">
                                  {testimonial.full_name}
                                </h3>
                                <p className="text-xs sm:text-sm text-gray-600 truncate">{testimonial.location}</p>
                                <div className="flex items-center gap-2 mt-1">
                                  <span className="text-yellow-500 text-sm">{renderStars(testimonial.rating)}</span>
                                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                                    testimonial.is_active
                                      ? 'bg-green-100 text-green-800'
                                      : 'bg-gray-100 text-gray-800'
                                  }`}>
                                    {testimonial.is_active ? 'Active' : 'Inactive'}
                                  </span>
                                </div>
                              </div>
                            </div>

                            <div className="grid grid-cols-2 gap-2 sm:gap-3 text-xs sm:text-sm mb-3">
                              <div>
                                <span className="text-gray-500">Date:</span>
                                <p className="text-gray-900 font-medium">{formatDate(testimonial.created_at)}</p>
                              </div>
                              <div className="col-span-2">
                                <span className="text-gray-500">Message:</span>
                                <p className="text-gray-900 font-medium line-clamp-2">{testimonial.message}</p>
                              </div>
                            </div>

                            <div className="flex gap-2">
                              <button
                                onClick={() => navigate(`/testimonials/view/${testimonial.id}`)}
                                className="flex-1 px-3 sm:px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors text-xs sm:text-sm font-medium"
                              >
                                👁️ View
                              </button>
                              <button
                                onClick={() => navigate(`/testimonials/edit/${testimonial.id}`)}
                                className="flex-1 px-3 sm:px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-xs sm:text-sm font-medium"
                              >
                                ✏️ Edit
                              </button>
                              <button
                                onClick={() => handleDelete(testimonial.id, testimonial.full_name)}
                                className="px-3 sm:px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors text-xs sm:text-sm font-medium"
                              >
                                🗑️
                              </button>
                            </div>
                          </div>
                        ))}

                        {/* Mobile Pagination */}
                        <div className="bg-white p-3 sm:p-4 rounded-lg shadow-sm">
                          <div className="text-xs sm:text-sm text-gray-700 text-center mb-3">
                            Showing {testimonials.length} of {pagination.total} - Page {pagination.page} of {pagination.pages}
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => handlePageChange(pagination.page - 1)}
                              disabled={pagination.page === 1}
                              className="flex-1 px-3 sm:px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition text-xs sm:text-sm font-medium"
                            >
                              ← Previous
                            </button>
                            <button
                              onClick={() => handlePageChange(pagination.page + 1)}
                              disabled={pagination.page === pagination.pages}
                              className="flex-1 px-3 sm:px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition text-xs sm:text-sm font-medium"
                            >
                              Next →
                            </button>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </main>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TestimonialList;